import { Button, Title } from "@mantine/core";
import {
  IconArrowUpRight,
  IconChevronRight,
  IconFileText,
  IconForms,
  IconLayoutList,
  IconMenu2,
  IconMicrophone2,
  IconPalette,
  IconPhoto,
  IconWorld,
} from "@tabler/icons-react";
import type { ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { usePermission } from "@how/app-kit";
import type { WebsiteTab } from "../WebsiteTabs";
import { useAssets, usePages, usePublicHost } from "../website-queries";
import { websiteHref } from "../website-variant";
import "./studio.css";

const SECTIONS: readonly {
  readonly tab: WebsiteTab;
  readonly label: string;
  readonly hint: string;
  readonly path: string;
  readonly icon: ReactNode;
}[] = [
  {
    tab: "pages",
    label: "Pages",
    hint: "What visitors read",
    path: "/website",
    icon: <IconFileText size={18} stroke={1.8} />,
  },
  {
    tab: "menus",
    label: "Menus",
    hint: "How they find their way",
    path: "/website/menus",
    icon: <IconMenu2 size={18} stroke={1.8} />,
  },
  {
    tab: "images",
    label: "Images",
    hint: "Your photo library",
    path: "/website/images",
    icon: <IconPhoto size={18} stroke={1.8} />,
  },
  {
    tab: "look",
    label: "Look",
    hint: "Theme, colours, type",
    path: "/website/look",
    icon: <IconPalette size={18} stroke={1.8} />,
  },
  {
    tab: "forms",
    label: "Forms",
    hint: "What visitors send",
    path: "/website/forms",
    icon: <IconForms size={18} stroke={1.8} />,
  },
  {
    tab: "sermons",
    label: "Sermons",
    hint: "Library and podcast",
    path: "/website/sermons",
    icon: <IconMicrophone2 size={18} stroke={1.8} />,
  },
  {
    tab: "domain",
    label: "Domain",
    hint: "Your own address",
    path: "/website/domain",
    icon: <IconWorld size={18} stroke={1.8} />,
  },
];

/** The classic design's address for the screen on show: the same path without /redesign. */
function useClassicHref(): string {
  const location = useLocation();
  return `${location.pathname.replace(/\/redesign\/?$/, "") || "/website"}${location.search}`;
}

/** The site's own address and a way to open it, on the blue banner every studio screen starts with. */
function SiteBanner({ heading }: { readonly heading: ReactNode }) {
  const host = usePublicHost();
  const pages = usePages();
  const live = (pages.data ?? []).filter((p) => p.published_at !== null).length;
  return (
    <header className="ws-banner">
      <div className="ws-banner__main">
        <span className="ws-banner__icon" aria-hidden>
          <IconWorld size={22} stroke={1.8} />
        </span>
        <div className="ws-banner__text">
          {heading}
          {host.data == null ? (
            <p className="ws-banner__host">Your church&apos;s public website</p>
          ) : (
            <p className="ws-banner__host">
              <span className="ws-banner__dot" aria-hidden />
              <span>{host.data}</span>
              {pages.data === undefined ? null : (
                <span className="ws-banner__meta">
                  · {live} of {pages.data.length} {pages.data.length === 1 ? "page" : "pages"} live
                </span>
              )}
            </p>
          )}
        </div>
      </div>
      {host.data == null ? null : (
        <Button
          component="a"
          href={`https://${host.data}`}
          target="_blank"
          rel="noreferrer"
          className="ws-banner__visit"
          rightSection={<IconArrowUpRight size={16} />}
        >
          View site
        </Button>
      )}
    </header>
  );
}

/** The sections, down the side on a wide screen and across the top on a phone. */
function SectionRail({ active }: { readonly active: WebsiteTab }) {
  const pages = usePages();
  const assets = useAssets();
  const canManageForms = usePermission("forms.manage");
  const canManageSettings = usePermission("settings.manage");
  const canManageSermons = usePermission("sermons.manage", { module: "sermons_media" });
  const classic = useClassicHref();
  const shown = SECTIONS.filter(
    (s) =>
      (s.tab !== "forms" || canManageForms.data === true) &&
      (s.tab !== "sermons" || canManageSermons.data === true) &&
      (s.tab !== "domain" || canManageSettings.data === true),
  );
  const count = (tab: WebsiteTab): number | null =>
    tab === "pages"
      ? (pages.data?.length ?? null)
      : tab === "images"
        ? (assets.data?.length ?? null)
        : null;
  return (
    <nav className="ws-rail" aria-label="Website sections">
      <ul className="ws-rail__list">
        {shown.map((s) => {
          const n = count(s.tab);
          const current = s.tab === active;
          return (
            <li key={s.tab}>
              <Link
                to={websiteHref("studio", s.path)}
                className="ws-rail__item"
                aria-current={current ? "page" : undefined}
              >
                <span className="ws-rail__icon" aria-hidden>
                  {s.icon}
                </span>
                <span className="ws-rail__text">
                  <span className="ws-rail__label">{s.label}</span>
                  <span className="ws-rail__hint">{s.hint}</span>
                </span>
                {n === null ? null : <span className="ws-rail__count">{n}</span>}
              </Link>
            </li>
          );
        })}
      </ul>
      <Link to={classic} className="ws-rail__classic">
        <IconLayoutList size={16} stroke={1.8} aria-hidden />
        Classic view
      </Link>
    </nav>
  );
}

export interface StudioShellProps {
  readonly section: WebsiteTab;
  /** The section's heading (h2), under the banner's h1. */
  readonly title: string;
  readonly description?: ReactNode;
  /** The section's own buttons, beside its heading. */
  readonly actions?: ReactNode;
  readonly children: ReactNode;
}

/**
 * The Website section in the studio design (preview, 2026-10-13): a blue banner with the site's address and a way to
 * open it, the sections in a rail beside the work, and each section's heading and actions over its content.
 */
export function StudioShell({ section, title, description, actions, children }: StudioShellProps) {
  return (
    <div className="ws-studio">
      <SiteBanner
        heading={
          <Title order={1} className="ws-banner__title">
            Website
          </Title>
        }
      />
      <div className="ws-layout">
        <SectionRail active={section} />
        <section className="ws-section" aria-labelledby="ws-section-title">
          <div className="ws-section__head">
            <div className="ws-section__titles">
              <Title order={2} className="ws-section__title" id="ws-section-title">
                {title}
              </Title>
              {description === undefined ? null : (
                <p className="ws-section__description">{description}</p>
              )}
            </div>
            {actions === undefined || actions === null ? null : (
              <div className="ws-section__actions">{actions}</div>
            )}
          </div>
          {children}
        </section>
      </div>
    </div>
  );
}

export interface StudioEditorShellProps {
  /** Where the record belongs: Pages or Forms, as a link back. */
  readonly section: { readonly label: string; readonly path: string };
  readonly title: string;
  /** Beside the title: the record's status. */
  readonly badges?: ReactNode;
  readonly subtitle?: ReactNode;
  readonly actions?: ReactNode;
  readonly children: ReactNode;
}

/** A page or a form being edited in the studio design: a trail back, the record's name large, its actions, the work. */
export function StudioEditorShell({
  section,
  title,
  badges,
  subtitle,
  actions,
  children,
}: StudioEditorShellProps) {
  const classic = useClassicHref();
  return (
    <div className="ws-studio">
      <header className="ws-editor-head">
        <nav aria-label="Breadcrumb" className="ws-crumbs">
          <Link to={websiteHref("studio", "/website")}>Website</Link>
          <IconChevronRight size={14} aria-hidden />
          <Link to={websiteHref("studio", section.path)}>{section.label}</Link>
          <IconChevronRight size={14} aria-hidden />
          <span aria-current="page">{title}</span>
          <Link to={classic} className="ws-crumbs__classic">
            Classic view
          </Link>
        </nav>
        <div className="ws-editor-head__row">
          <div className="ws-editor-head__titles">
            <div className="ws-editor-head__title-line">
              <Title order={1} className="ws-editor-head__title">
                {title}
              </Title>
              {badges}
            </div>
            {subtitle === undefined ? null : (
              <div className="ws-editor-head__subtitle">{subtitle}</div>
            )}
          </div>
          {actions === undefined || actions === null ? null : (
            <div className="ws-section__actions">{actions}</div>
          )}
        </div>
      </header>
      {children}
    </div>
  );
}
